import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock, SectionCard, SeverityBadge } from "../../components/primitives";
import { formatCount, formatStamp } from "../../format";
import type { BbReason, BbSection, BranchMeta } from "../../types";
import { Chip, PizzaRef } from "../pipeline/shared";
import { StepsLine } from "./bits";
import { REASON_TONE, outcomeLabel, reasonHint, reasonLabel } from "./helpers";

type BranchOf = (id: string | null | undefined) => BranchMeta | null;

const CHIP_ACTIVE = "ring-1 ring-foreground/40";

/**
 * Finished pizzas (packed, or their order went past the kitchen) without a capture time, each with the
 * most likely reason and the webhook evidence behind it; filterable by reason, opens the pizza timeline.
 */
export function BbUncapturedCard({
  data,
  branchOf,
  onOpen,
}: {
  data: BbSection;
  branchOf: BranchOf;
  onOpen: (itemId: string) => void;
}) {
  const { t } = useLocalization();
  const [reason, setReason] = useState<BbReason | null>(null);
  const { items, total, by_reason: byReason, reasons } = data.uncaptured;
  const present = useMemo(
    () => reasons.filter((r) => (byReason[r] ?? 0) > 0).sort((a, b) => (byReason[b] ?? 0) - (byReason[a] ?? 0)),
    [reasons, byReason],
  );
  const shown = reason ? items.filter((item) => item.reason === reason) : items;
  const name = (id: string | null) => branchOf(id)?.name || id || t("monitoring.unknownBranch");
  const severity = present.some((r) => REASON_TONE[r] === "nok") ? "critical" : "warning";

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          {t("monitoring.bb.uncaptured.title")}
          {total > 0 && <SeverityBadge severity={severity} label={formatCount(total)} />}
        </span>
      }
      description={t("monitoring.bb.uncaptured.description")}
      contentClassName="p-0"
    >
      {total === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.bb.uncaptured.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 border-b px-4 py-2.5" role="group" aria-label={t("monitoring.bb.log.outcome")}>
            <button
              type="button"
              onClick={() => setReason(null)}
              aria-pressed={reason === null}
              className={cn("rounded", reason === null && CHIP_ACTIVE)}
            >
              <Chip>
                {t("monitoring.bb.uncaptured.all")} <span className="tabular-nums">{formatCount(total)}</span>
              </Chip>
            </button>
            {present.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(reason === r ? null : r)}
                aria-pressed={reason === r}
                title={reasonHint(r)}
                className={cn("rounded", reason === r && CHIP_ACTIVE)}
              >
                <Chip tone={REASON_TONE[r]}>
                  {reasonLabel(r)} <span className="tabular-nums">{formatCount(byReason[r] ?? 0)}</span>
                </Chip>
              </button>
            ))}
          </div>
          {shown.length === 0 ? (
            <div className="p-4">
              <EmptyBlock>{t("monitoring.bb.uncaptured.emptyFilter")}</EmptyBlock>
            </div>
          ) : (
            <ul className="max-h-[34rem] divide-y divide-border/60 overflow-y-auto">
              {shown.map((pizza) => {
                const when = pizza.packed_at ?? pizza.sent_at;
                return (
                  <li key={pizza.id} className="flex flex-col gap-1.5 px-4 py-2.5">
                    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                      <PizzaRef id={pizza.id} name={pizza.name} className="flex-1" />
                      <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                        <span>
                          <span className="text-muted-foreground">
                            {pizza.packed_at ? t("monitoring.bb.uncaptured.packedAt") : t("monitoring.bb.uncaptured.orderedAt")}{" "}
                          </span>
                          <Ago ts={when} />
                        </span>
                        <span className="text-[11px] tabular-nums text-muted-foreground">{formatStamp(when)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <Chip tone={REASON_TONE[pizza.reason]} title={reasonHint(pizza.reason)}>
                        {reasonLabel(pizza.reason)}
                      </Chip>
                      <span className="break-words text-muted-foreground">{name(pizza.branch_id)}</span>
                      {pizza.order_status && (
                        <span className="text-[11px] text-muted-foreground">
                          {t("monitoring.bb.uncaptured.orderStatus")} <span className="font-mono">{pizza.order_status}</span>
                        </span>
                      )}
                      {pizza.has_photo && <Chip>{t("monitoring.bb.uncaptured.hasPhoto")}</Chip>}
                      {!pizza.logged && (
                        <Chip title={t("monitoring.bb.uncaptured.preLogHint")}>{t("monitoring.bb.uncaptured.preLog")}</Chip>
                      )}
                    </div>
                    <p className="text-[11px] leading-snug text-muted-foreground">{reasonHint(pizza.reason)}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {pizza.logged && (
                        <span>
                          {t("monitoring.bb.uncaptured.hooks")}{" "}
                          <span className="font-mono tabular-nums text-foreground">{pizza.hooks}</span>
                          {pizza.packed_hooks > 0 && (
                            <> · {t("monitoring.bb.uncaptured.packedHooks", { count: pizza.packed_hooks })}</>
                          )}
                          {pizza.last_outcome && <> · {outcomeLabel(pizza.last_outcome)}</>}
                        </span>
                      )}
                      {pizza.last_steps && (
                        <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
                          {t("monitoring.bb.uncaptured.lastSteps")} <StepsLine steps={pizza.last_steps} />
                        </span>
                      )}
                      {pizza.detail && <span className="break-all font-mono">{pizza.detail}</span>}
                      <span className="ml-auto inline-flex items-center gap-2">
                        {pizza.order_id && (
                          <Link to={`/orders/${pizza.order_id}`} className="hover:text-foreground hover:underline">
                            {t("monitoring.pipeline.order")}
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5 px-2 text-xs"
                          onClick={() => onOpen(pizza.id)}
                        >
                          <History className="h-3.5 w-3.5" aria-hidden />
                          {t("monitoring.bb.uncaptured.timeline")}
                        </Button>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {total > items.length && (
            <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">
              {t("monitoring.bb.uncaptured.more", { shown: items.length, total })}
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}
